import type {
  GridViewPayload,
  GridViewSystemView,
  ResolvedGridViewState,
} from '../types'
import type {
  GridViewItem,
  GridViewPreferenceItem,
} from '@/services/gridView/gridView.types'
import { parseGridViewSelectedRef } from '@/services/gridView/gridView.types'

import {
  areGridViewPayloadsEqual,
  isCompatibleGridViewPayloadVersion,
  parseGridViewPayload,
  reconcileGridViewPayload,
  stableSerializeGridViewPayload,
} from './payload'

const sortViewsByUpdatedAtDesc = (left: GridViewItem, right: GridViewItem) =>
  (right.updatedAt ?? '').localeCompare(left.updatedAt ?? '')

const arePayloadsSame = (left: GridViewPayload, right: GridViewPayload) =>
  areGridViewPayloadsEqual(left, right)

const parseCompatibleViewPayload = ({
  payload,
  payloadVersion,
  gridSchemaVersion,
  currentGridSchemaVersion,
  systemPayload,
  contextualExternalFilterFields,
  externalFilterFields,
}: {
  payload?: string | null
  payloadVersion?: string | null
  gridSchemaVersion?: string | null
  currentGridSchemaVersion: string
  systemPayload: GridViewPayload
  contextualExternalFilterFields?: readonly string[]
  externalFilterFields?: readonly string[]
}): GridViewPayload | null => {
  if (!isCompatibleGridViewPayloadVersion(payloadVersion)) return null
  const schemaDrifted = gridSchemaVersion !== currentGridSchemaVersion
  const parsedPayload = parseGridViewPayload(payload, {
    contextualExternalFilterFields,
    rejectMalformed: schemaDrifted,
  })
  if (!parsedPayload) return null

  if (!schemaDrifted) return parsedPayload

  // A schema-version change is not by itself destructive: the current system
  // payload is the authoritative column contract and can safely reconcile
  // obsolete columns. Ambiguous sort/filter references still fail closed.
  return reconcileGridViewPayload({
    payload: parsedPayload,
    systemPayload,
    contextualExternalFilterFields,
    externalFilterFields,
  })
}

export const resolveEffectiveGridViewState = ({
  availableViews,
  preference,
  systemPayload,
  systemViews = [],
  preferDefinitionFallback = false,
  currentGridSchemaVersion,
  contextualExternalFilterFields,
  externalFilterFields,
}: {
  availableViews: GridViewItem[]
  preference?: GridViewPreferenceItem | null
  systemPayload: GridViewPayload
  systemViews?: GridViewSystemView[]
  preferDefinitionFallback?: boolean
  currentGridSchemaVersion: string
  contextualExternalFilterFields?: readonly string[]
  externalFilterFields?: readonly string[]
}): ResolvedGridViewState => {
  const activeViews = [...availableViews]
    .filter((view) => view.status !== 'deleted')
    .sort(sortViewsByUpdatedAtDesc)

  const selectedRef = parseGridViewSelectedRef(preference?.selectedViewRef)
  const isPersonalDraftInactive =
    'isPersonalDraftInactive' in selectedRef &&
    selectedRef.isPersonalDraftInactive
  const selectedView =
    selectedRef.type === 'team'
      ? (activeViews.find((view) => view.id === selectedRef.id) ?? null)
      : null
  const defaultView = activeViews.find((view) => view.isDefault) ?? null
  const rawSelectedSystemView =
    selectedRef.type === 'system'
      ? (systemViews.find(
          (view) => view.isCompatible && view.key === selectedRef.key,
        ) ?? null)
      : null
  const selectedSystemView =
    preferDefinitionFallback &&
    selectedRef.type === 'system' &&
    rawSelectedSystemView?.isDefault &&
    !selectedRef.isExplicitSelection
      ? null
      : rawSelectedSystemView

  const selectedViewPayload = selectedView
    ? parseCompatibleViewPayload({
        payload: selectedView.payload,
        payloadVersion: selectedView.payloadVersion,
        gridSchemaVersion: selectedView.gridSchemaVersion,
        currentGridSchemaVersion,
        systemPayload,
        contextualExternalFilterFields,
        externalFilterFields,
      })
    : null

  const defaultViewPayload = defaultView
    ? parseCompatibleViewPayload({
        payload: defaultView.payload,
        payloadVersion: defaultView.payloadVersion,
        gridSchemaVersion: defaultView.gridSchemaVersion,
        currentGridSchemaVersion,
        systemPayload,
        contextualExternalFilterFields,
        externalFilterFields,
      })
    : null

  const personalDraftPayload = parseCompatibleViewPayload({
    payload: preference?.draftPayload,
    payloadVersion: preference?.payloadVersion,
    gridSchemaVersion: preference?.gridSchemaVersion,
    currentGridSchemaVersion,
    systemPayload,
    contextualExternalFilterFields,
    externalFilterFields,
  })

  if (selectedRef.type === 'personal' && personalDraftPayload) {
    return {
      source: 'personal-draft',
      currentKind: 'personal',
      activeSharedView: null,
      activeSystemView: null,
      payload: personalDraftPayload,
      isModified: true,
      signature: `personal:active:${stableSerializeGridViewPayload(personalDraftPayload)}`,
    }
  }

  if (
    personalDraftPayload &&
    selectedRef.type !== 'personal' &&
    !isPersonalDraftInactive
  ) {
    if (
      selectedView &&
      selectedViewPayload &&
      arePayloadsSame(personalDraftPayload, selectedViewPayload)
    ) {
      return {
        source: 'selected-view',
        currentKind: 'team',
        activeSharedView: selectedView,
        activeSystemView: null,
        payload: selectedViewPayload,
        isModified: false,
        signature: `selected:${selectedView.id}:${stableSerializeGridViewPayload(selectedViewPayload)}`,
      }
    }

    if (
      !preferDefinitionFallback &&
      defaultView &&
      defaultViewPayload &&
      arePayloadsSame(personalDraftPayload, defaultViewPayload)
    ) {
      return {
        source: 'default-view',
        currentKind: 'team',
        activeSharedView: defaultView,
        activeSystemView: null,
        payload: defaultViewPayload,
        isModified: false,
        signature: `default:${defaultView.id}:${stableSerializeGridViewPayload(defaultViewPayload)}`,
      }
    }

    if (
      selectedSystemView &&
      arePayloadsSame(personalDraftPayload, selectedSystemView.payload)
    ) {
      return {
        source: 'system-preset',
        currentKind: 'system',
        activeSharedView: null,
        activeSystemView: selectedSystemView,
        payload: selectedSystemView.payload,
        isModified: false,
        signature: `system-preset:${selectedSystemView.key}:${stableSerializeGridViewPayload(selectedSystemView.payload)}`,
      }
    }

    if (
      preferDefinitionFallback &&
      arePayloadsSame(personalDraftPayload, systemPayload)
    ) {
      return {
        source: 'definition',
        currentKind: 'system',
        activeSharedView: null,
        activeSystemView: null,
        payload: systemPayload,
        isModified: false,
        signature: `definition:${stableSerializeGridViewPayload(systemPayload)}`,
      }
    }

    const matchingSystemView =
      systemViews.find(
        (view) =>
          view.isCompatible &&
          arePayloadsSame(view.payload, personalDraftPayload),
      ) ?? null
    const isDefaultSystemSelection =
      matchingSystemView?.isDefault &&
      (selectedRef.type === 'default' ||
        (selectedRef.type === 'system' &&
          !selectedRef.isExplicitSelection &&
          rawSelectedSystemView?.key === matchingSystemView.key))

    if (preferDefinitionFallback && isDefaultSystemSelection) {
      return {
        source: 'definition',
        currentKind: 'system',
        activeSharedView: null,
        activeSystemView: null,
        payload: systemPayload,
        isModified: false,
        signature: `definition:${stableSerializeGridViewPayload(systemPayload)}`,
      }
    }

    if (matchingSystemView && selectedRef.type === 'default') {
      return {
        source: 'system-preset',
        currentKind: 'system',
        activeSharedView: null,
        activeSystemView: matchingSystemView,
        payload: matchingSystemView.payload,
        isModified: false,
        signature: `system-preset:${matchingSystemView.key}:${stableSerializeGridViewPayload(matchingSystemView.payload)}`,
      }
    }

    if (arePayloadsSame(personalDraftPayload, systemPayload)) {
      return {
        source: 'system',
        currentKind: 'system',
        activeSharedView: null,
        activeSystemView: null,
        payload: systemPayload,
        isModified: false,
        signature: `system:${stableSerializeGridViewPayload(systemPayload)}`,
      }
    }

    const activeSharedView = selectedViewPayload ? selectedView : null
    const activeSystemView = selectedSystemView ?? null

    return {
      source: 'personal-draft',
      currentKind: activeSharedView
        ? 'team'
        : activeSystemView
          ? 'system'
          : 'personal',
      activeSharedView,
      activeSystemView,
      payload: personalDraftPayload,
      isModified: true,
      signature: `personal:${activeSharedView?.id ?? activeSystemView?.key ?? 'none'}:${stableSerializeGridViewPayload(personalDraftPayload)}`,
    }
  }

  if (selectedView && selectedViewPayload) {
    return {
      source: 'selected-view',
      currentKind: 'team',
      activeSharedView: selectedView,
      activeSystemView: null,
      payload: selectedViewPayload,
      isModified: false,
      signature: `selected:${selectedView.id}:${stableSerializeGridViewPayload(selectedViewPayload)}`,
    }
  }

  if (selectedSystemView) {
    return {
      source: 'system-preset',
      currentKind: 'system',
      activeSharedView: null,
      activeSystemView: selectedSystemView,
      payload: selectedSystemView.payload,
      isModified: false,
      signature: `system-preset:${selectedSystemView.key}:${stableSerializeGridViewPayload(selectedSystemView.payload)}`,
    }
  }

  if (preferDefinitionFallback) {
    return {
      source: 'definition',
      currentKind: 'system',
      activeSharedView: null,
      activeSystemView: null,
      payload: systemPayload,
      isModified: false,
      signature: `definition:${stableSerializeGridViewPayload(systemPayload)}`,
    }
  }

  if (defaultView && defaultViewPayload) {
    return {
      source: 'default-view',
      currentKind: 'team',
      activeSharedView: defaultView,
      activeSystemView: null,
      payload: defaultViewPayload,
      isModified: false,
      signature: `default:${defaultView.id}:${stableSerializeGridViewPayload(defaultViewPayload)}`,
    }
  }

  const systemView = systemViews.find((view) => view.isCompatible) ?? null
  if (systemView) {
    return {
      source: 'system-preset',
      currentKind: 'system',
      activeSharedView: null,
      activeSystemView: systemView,
      payload: systemView.payload,
      isModified: false,
      signature: `system-preset:${systemView.key}:${stableSerializeGridViewPayload(systemView.payload)}`,
    }
  }

  return {
    source: 'system',
    currentKind: 'system',
    activeSharedView: null,
    activeSystemView: null,
    payload: systemPayload,
    isModified: false,
    signature: `system:${stableSerializeGridViewPayload(systemPayload)}`,
  }
}
